const React = require('react');
const { createElement: h, useState, useRef, useEffect } = React;
const { getApiOrigin } = require('../lib/apiClient');
require('./ApiDocsPage.css');

var BASE = '';

function getBase() {
  if (BASE) return BASE;
  BASE = (getApiOrigin() || window.location.origin);
  return BASE;
}

// The API is mounted at <origin>/api, EXCEPT /v1 which sits at the root.
// In production the API and the web app are different domains, so the
// consent-screen URL must come from the app origin, not the API origin.
function getApiBase() {
  return getBase() + '/api';
}

function getSiteOrigin() {
  return (typeof window !== 'undefined' && window.location) ? window.location.origin : '';
}

/* ── tiny helpers ───────────────────────────────────────── */

function MethodBadge(props) {
  var method = String(props && props.method || 'GET').toUpperCase();
  var cls = 'adoc-method adoc-method--' + method.toLowerCase();
  return h('span', { className: cls }, method);
}

function CodeBlock(props) {
  var code = props && props.code || '';
  return h('pre', { className: 'adoc-code' }, h('code', null, code));
}

function ParamRow(props) {
  return h('tr', null,
    h('td', null, h('code', { className: 'adoc-param-name' }, props.name), props.required ? h('span', { className: 'adoc-param-req' }, 'required') : null),
    h('td', null, h('span', { className: 'adoc-param-type' }, props.type)),
    h('td', null, props.description)
  );
}

function ParamTable(props) {
  var params = props && props.params || [];
  if (!params.length) return null;
  return h('div', { className: 'adoc-param-table-wrap' },
    h('table', { className: 'adoc-param-table' },
      h('thead', null,
        h('tr', null,
          h('th', null, 'Parameter'),
          h('th', null, 'Type'),
          h('th', null, 'Description')
        )
      ),
      h('tbody', null, params.map(function(p, i) {
        return h(ParamRow, Object.assign({ key: i }, p));
      }))
    )
  );
}

function ResponseBlock(props) {
  var status = props && props.status || 200;
  var body = props && props.body || '';
  return h('div', { className: 'adoc-response' },
    h('div', { className: 'adoc-response__head' },
      h('span', { className: 'adoc-response__status adoc-response__status--' + (status < 300 ? 'ok' : 'err') }, String(status)),
      h('span', { className: 'adoc-response__label' }, props && props.label || 'Response')
    ),
    h(CodeBlock, { code: body })
  );
}

/* ── sections data ──────────────────────────────────────── */

function buildSections() {
  var base = getBase();
  var apiBase = getApiBase();
  var siteOrigin = getSiteOrigin();
  return [
    {
      id: 'getting-started',
      group: 'Getting started',
      title: 'Introduction',
      body: function() {
        return h('div', null,
          h('p', { className: 'adoc-text' }, 'The Alleflow API lets you integrate workflow automation into your own applications. You can create workflows, list existing ones, and retrieve workflow details — all through simple REST endpoints.'),
          h('h3', { className: 'adoc-h3' }, 'Base URL'),
          h(CodeBlock, { code: base + '/v1' }),
          h('h3', { className: 'adoc-h3' }, 'Content type'),
          h('p', { className: 'adoc-text' }, 'All request and response bodies use JSON. Set the Content-Type header to application/json for POST/PUT requests.')
        );
      }
    },
    {
      id: 'authentication',
      group: 'Getting started',
      title: 'Authentication',
      body: function() {
        return h('div', null,
          h('p', { className: 'adoc-text' }, 'The /v1 API accepts two kinds of credentials. Use an API key when your code acts on your own account. Use OAuth when your app acts on behalf of OTHER Alleflow users.'),
          h('h3', { className: 'adoc-h3' }, 'API key — your own account'),
          h('p', { className: 'adoc-text' }, 'Generate keys from the API Keys page in your dashboard. An API key has no scope restrictions.'),
          h(CodeBlock, { code: 'X-API-Key: af_your_api_key_here' }),
          h(CodeBlock, { code: 'curl -H "X-API-Key: af_abc12345..." \\\n  ' + base + '/v1/workflows' }),
          h('h3', { className: 'adoc-h3' }, 'OAuth token — another user’s account'),
          h('p', { className: 'adoc-text' }, 'Register an app on the Developer page, send the user through the authorization flow, then call the API with the resulting access token. The token only works for the scopes that user approved.'),
          h(CodeBlock, { code: 'Authorization: Bearer afa_your_access_token' }),
          h('h3', { className: 'adoc-h3' }, 'Error responses'),
          h('p', { className: 'adoc-text' }, 'If credentials are missing or invalid:'),
          h(ResponseBlock, { status: 401, label: 'Unauthorized', body: '{\n  "error": "Missing credentials. Send an X-API-Key header or an OAuth Bearer token."\n}' }),
          h('p', { className: 'adoc-text' }, 'If the token is valid but lacks the scope the endpoint requires:'),
          h(ResponseBlock, { status: 403, label: 'Forbidden', body: '{\n  "error": "This access token is missing the required scope.",\n  "required_scope": "workflows:write",\n  "granted_scopes": ["openid", "profile", "workflows:read"]\n}' })
        );
      }
    },
    {
      id: 'errors',
      group: 'Getting started',
      title: 'Errors',
      body: function() {
        return h('div', null,
          h('p', { className: 'adoc-text' }, 'The API uses standard HTTP status codes. Errors return a JSON body with an error field.'),
          h(ParamTable, { params: [
            { name: '200', type: 'OK', description: 'Request succeeded.' },
            { name: '201', type: 'Created', description: 'Resource created.' },
            { name: '204', type: 'No Content', description: 'Deleted / updated with no body.' },
            { name: '400', type: 'Bad Request', description: 'Missing or invalid parameters.' },
            { name: '401', type: 'Unauthorized', description: 'Invalid or missing API key.' },
            { name: '404', type: 'Not Found', description: 'Resource does not exist.' },
            { name: '500', type: 'Server Error', description: 'Something went wrong on our side.' }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Error body'),
          h(CodeBlock, { code: '{\n  "error": "Human-readable description of what went wrong."\n}' })
        );
      }
    },
    {
      id: 'oauth-overview',
      group: 'OAuth',
      title: 'Overview',
      body: function() {
        return h('div', null,
          h('p', { className: 'adoc-text' }, 'Alleflow is an OAuth 2.0 provider. Any Alleflow user can sign in to your app and let it work with their workflows, without ever handing you their password or an API key.'),
          h('h3', { className: 'adoc-h3' }, 'The flow'),
          h(CodeBlock, { code: [
            '1. You register an app on the Developer page and get',
            '   a client_id + client_secret.',
            '',
            '2. You send the user to /oauth/authorize. They see a',
            '   consent screen listing exactly what you asked for.',
            '',
            '3. They approve. Their browser is redirected back to your',
            '   redirect_uri with ?code=...',
            '',
            '4. Your server POSTs that code to /oauth/token and gets',
            '   an access_token + refresh_token.',
            '',
            '5. You call /v1 with the access token as a Bearer token.'
          ].join('\n') }),
          h('h3', { className: 'adoc-h3' }, 'Endpoints'),
          h(ParamTable, { params: [
            { name: 'GET /oauth/authorize', type: 'browser', description: 'Consent screen. Send the user here.' },
            { name: 'POST /api/oauth/token', type: 'server', description: 'Exchange a code, or refresh a token.' },
            { name: 'POST /api/oauth/revoke', type: 'server', description: 'Revoke an access or refresh token.' },
            { name: 'GET /v1/userinfo', type: 'server', description: 'Identity of the user the token belongs to.' }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Token lifetimes'),
          h(ParamTable, { params: [
            { name: 'Authorization code', type: '10 minutes', description: 'Single use. Consumed at exchange.' },
            { name: 'Access token', type: '1 hour', description: 'Sent as a Bearer token on /v1 calls.' },
            { name: 'Refresh token', type: '30 days', description: 'Rotates on every use — the old one stops working.' }
          ] })
        );
      }
    },
    {
      id: 'oauth-scopes',
      group: 'OAuth',
      title: 'Scopes',
      body: function() {
        return h('div', null,
          h('p', { className: 'adoc-text' }, 'A scope is one permission. You pick which scopes your app may request when you register it; the user then approves them on the consent screen. Requesting a scope your app is not registered for is silently narrowed away, not granted.'),
          h(ParamTable, { params: [
            { name: 'openid', type: 'identity', description: 'Confirm which Alleflow account signed in.' },
            { name: 'profile', type: 'identity', description: 'Read username, email and plan via /v1/userinfo.' },
            { name: 'workflows:read', type: 'workflows', description: 'List workflows and read their nodes and connections.' },
            { name: 'workflows:write', type: 'workflows', description: 'Create new workflows. Cannot delete existing ones.' }
          ] }),
          h('p', { className: 'adoc-text' }, 'Separate multiple scopes with spaces (URL-encoded as %20) in the authorize URL.')
        );
      }
    },
    {
      id: 'oauth-authorize',
      group: 'OAuth',
      title: 'Authorize',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'GET' }),
            h('code', { className: 'adoc-endpoint-path' }, '/oauth/authorize')
          ),
          h('p', { className: 'adoc-text' }, 'Send the user here in a browser. This is a page, not an API call. After they approve, their browser is redirected to your redirect_uri carrying a one-time code.'),
          h('h3', { className: 'adoc-h3' }, 'Query parameters'),
          h(ParamTable, { params: [
            { name: 'client_id', type: 'string', description: 'From the Developer page.', required: true },
            { name: 'redirect_uri', type: 'string', description: 'Must match one of your registered URIs EXACTLY.', required: true },
            { name: 'response_type', type: 'string', description: 'Must be "code".', required: true },
            { name: 'scope', type: 'string', description: 'Space-separated scopes, e.g. "openid profile workflows:read".', required: true },
            { name: 'state', type: 'string', description: 'Random value you generate. Returned unchanged — check it to defend against CSRF.', required: false },
            { name: 'code_challenge', type: 'string', description: 'PKCE challenge. Required for apps that cannot keep a secret.', required: false },
            { name: 'code_challenge_method', type: 'string', description: 'S256 (recommended) or plain.', required: false }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Example'),
          h(CodeBlock, { code: siteOrigin + '/oauth/authorize\n  ?client_id=afc_abc123\n  &redirect_uri=https://example.com/oauth/callback\n  &response_type=code\n  &scope=openid%20profile%20workflows:read\n  &state=xyz789' }),
          h('h3', { className: 'adoc-h3' }, 'Success redirect'),
          h(CodeBlock, { code: 'https://example.com/oauth/callback?code=afg_...&state=xyz789' }),
          h('h3', { className: 'adoc-h3' }, 'If the user denies'),
          h(CodeBlock, { code: 'https://example.com/oauth/callback\n  ?error=access_denied\n  &error_description=The%20user%20denied%20the%20request.\n  &state=xyz789' })
        );
      }
    },
    {
      id: 'oauth-token',
      group: 'OAuth',
      title: 'Get a token',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'POST' }),
            h('code', { className: 'adoc-endpoint-path' }, '/api/oauth/token')
          ),
          h('p', { className: 'adoc-text' }, 'Server-side call. Trades the one-time code for tokens. Accepts form-encoded or JSON bodies. The code is single-use and expires after 10 minutes.'),
          h('h3', { className: 'adoc-h3' }, 'Body parameters'),
          h(ParamTable, { params: [
            { name: 'grant_type', type: 'string', description: 'Must be "authorization_code".', required: true },
            { name: 'code', type: 'string', description: 'The code from the redirect.', required: true },
            { name: 'client_id', type: 'string', description: 'Your client ID.', required: true },
            { name: 'client_secret', type: 'string', description: 'Your client secret. Omit only if you used PKCE.', required: false },
            { name: 'redirect_uri', type: 'string', description: 'The SAME URI you used at authorize time.', required: true },
            { name: 'code_verifier', type: 'string', description: 'PKCE verifier. Required if you sent a code_challenge.', required: false }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Request'),
          h(CodeBlock, { code: 'curl -X POST ' + apiBase + '/oauth/token \\\n  -d grant_type=authorization_code \\\n  -d code=afg_abc123 \\\n  -d client_id=afc_abc123 \\\n  -d client_secret=afs_secret \\\n  -d redirect_uri=https://example.com/oauth/callback' }),
          h('h3', { className: 'adoc-h3' }, 'Response'),
          h(ResponseBlock, { status: 200, label: 'Success', body: '{\n  "access_token": "afa_...",\n  "token_type": "Bearer",\n  "expires_in": 3600,\n  "refresh_token": "afr_...",\n  "scope": "openid profile workflows:read"\n}' }),
          h('p', { className: 'adoc-text' }, 'OAuth errors use the RFC 6749 body shape, not Alleflow’s usual { error } string:'),
          h(ResponseBlock, { status: 400, label: 'Bad request', body: '{\n  "error": "invalid_grant",\n  "error_description": "Authorization code is invalid or already used."\n}' })
        );
      }
    },
    {
      id: 'oauth-refresh',
      group: 'OAuth',
      title: 'Refresh a token',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'POST' }),
            h('code', { className: 'adoc-endpoint-path' }, '/api/oauth/token')
          ),
          h('p', { className: 'adoc-text' }, 'Access tokens last an hour. Use the refresh token to get a new one without prompting the user again.'),
          h('p', { className: 'adoc-text' }, 'Refresh tokens ROTATE: every refresh returns a NEW refresh token and invalidates the old one. Always store the newest value you received.'),
          h('h3', { className: 'adoc-h3' }, 'Body parameters'),
          h(ParamTable, { params: [
            { name: 'grant_type', type: 'string', description: 'Must be "refresh_token".', required: true },
            { name: 'refresh_token', type: 'string', description: 'The most recent refresh token you hold.', required: true },
            { name: 'client_id', type: 'string', description: 'Your client ID.', required: true },
            { name: 'client_secret', type: 'string', description: 'Your client secret, if your app has one.', required: false }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Request'),
          h(CodeBlock, { code: 'curl -X POST ' + apiBase + '/oauth/token \\\n  -d grant_type=refresh_token \\\n  -d refresh_token=afr_abc123 \\\n  -d client_id=afc_abc123 \\\n  -d client_secret=afs_secret' }),
          h('h3', { className: 'adoc-h3' }, 'Response'),
          h(ResponseBlock, { status: 200, label: 'Success', body: '{\n  "access_token": "afa_new...",\n  "token_type": "Bearer",\n  "expires_in": 3600,\n  "refresh_token": "afr_new...",\n  "scope": "openid profile workflows:read"\n}' })
        );
      }
    },
    {
      id: 'oauth-userinfo',
      group: 'OAuth',
      title: 'Userinfo',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'GET' }),
            h('code', { className: 'adoc-endpoint-path' }, '/v1/userinfo')
          ),
          h('p', { className: 'adoc-text' }, 'Returns the identity of the user the access token belongs to. This is what backs a "Sign in with Alleflow" button. Requires the profile scope.'),
          h('h3', { className: 'adoc-h3' }, 'Request'),
          h(CodeBlock, { code: 'curl -H "Authorization: Bearer afa_abc123" \\\n  ' + base + '/v1/userinfo' }),
          h('h3', { className: 'adoc-h3' }, 'Response'),
          h(ResponseBlock, { status: 200, label: 'Success', body: '{\n  "data": {\n    "sub": "6501f2a9c1d4e80012ab34cd",\n    "username": "jane",\n    "email": "jane@example.com",\n    "plan": "pro"\n  }\n}' }),
          h('p', { className: 'adoc-text' }, 'Use "sub" as the stable account identifier. Usernames and emails can change.')
        );
      }
    },
    {
      id: 'oauth-revoke',
      group: 'OAuth',
      title: 'Revoke a token',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'POST' }),
            h('code', { className: 'adoc-endpoint-path' }, '/api/oauth/revoke')
          ),
          h('p', { className: 'adoc-text' }, 'Invalidates a token immediately. Accepts either an access token or a refresh token — revoking either one ends that grant.'),
          h('h3', { className: 'adoc-h3' }, 'Body parameters'),
          h(ParamTable, { params: [
            { name: 'token', type: 'string', description: 'The access or refresh token to revoke.', required: true }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Request'),
          h(CodeBlock, { code: 'curl -X POST ' + apiBase + '/oauth/revoke \\\n  -d token=afa_abc123' }),
          h('h3', { className: 'adoc-h3' }, 'Response'),
          h('p', { className: 'adoc-text' }, 'Always returns 200, even for an unknown token, so this endpoint cannot be used to discover which tokens exist.'),
          h(ResponseBlock, { status: 200, label: 'Success', body: '{\n  "ok": true\n}' })
        );
      }
    },
    {
      id: 'oauth-pkce',
      group: 'OAuth',
      title: 'PKCE (public apps)',
      body: function() {
        return h('div', null,
          h('p', { className: 'adoc-text' }, 'Mobile apps and single-page apps cannot keep a client secret — anyone can read it out of the bundle. Those apps use PKCE instead, and omit client_secret entirely.'),
          h('h3', { className: 'adoc-h3' }, 'How it works'),
          h(CodeBlock, { code: [
            '1. Generate a random code_verifier (43-128 chars).',
            '',
            '2. Derive the challenge:',
            '   code_challenge = BASE64URL(SHA256(code_verifier))',
            '',
            '3. Send code_challenge + code_challenge_method=S256',
            '   on the authorize URL.',
            '',
            '4. Send the original code_verifier to /oauth/token.',
            '   It is checked against the challenge you committed to.'
          ].join('\n') }),
          h('h3', { className: 'adoc-h3' }, 'Generating the pair in Node'),
          h(CodeBlock, { code: [
            'var crypto = require(\'crypto\');',
            '',
            'var verifier = crypto.randomBytes(32).toString(\'base64url\');',
            'var challenge = crypto.createHash(\'sha256\')',
            '  .update(verifier)',
            '  .digest(\'base64url\');'
          ].join('\n') }),
          h('p', { className: 'adoc-text' }, 'A confidential (server-side) app should still send client_secret. PKCE and a client secret can be used together.')
        );
      }
    },
    {
      id: 'list-workflows',
      group: 'Workflows',
      title: 'List workflows',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'GET' }),
            h('code', { className: 'adoc-endpoint-path' }, '/v1/workflows')
          ),
          h('p', { className: 'adoc-text' }, 'Returns all workflows owned by the authenticated user, sorted by most recently updated.'),
          h('h3', { className: 'adoc-h3' }, 'Request'),
          h(CodeBlock, { code: 'curl -H "X-API-Key: YOUR_KEY" \\\n  ' + base + '/v1/workflows' }),
          h('h3', { className: 'adoc-h3' }, 'Response'),
          h(ResponseBlock, { status: 200, label: 'Success', body: '{\n  "data": [\n    {\n      "id": "wf_abc123",\n      "workflowName": "My Workflow",\n      "tags": [\n        { "name": "production", "color": "#10b981" }\n      ],\n      "createdAt": "2025-03-10T08:00:00.000Z",\n      "updatedAt": "2025-03-15T12:30:00.000Z"\n    }\n  ]\n}' })
        );
      }
    },
    {
      id: 'create-workflow',
      group: 'Workflows',
      title: 'Create workflow',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'POST' }),
            h('code', { className: 'adoc-endpoint-path' }, '/v1/workflows')
          ),
          h('p', { className: 'adoc-text' }, 'Creates a new empty workflow for the authenticated user.'),
          h('h3', { className: 'adoc-h3' }, 'Body parameters'),
          h(ParamTable, { params: [
            { name: 'workflowName', type: 'string', description: 'Name for the workflow. Defaults to "Untitled" if omitted.', required: false }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Request'),
          h(CodeBlock, { code: 'curl -X POST \\\n  -H "X-API-Key: YOUR_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d \'{ "workflowName": "Email Automation" }\' \\\n  ' + base + '/v1/workflows' }),
          h('h3', { className: 'adoc-h3' }, 'Response'),
          h(ResponseBlock, { status: 201, label: 'Created', body: '{\n  "data": {\n    "id": "wf_xyz789",\n    "workflowName": "Email Automation"\n  }\n}' })
        );
      }
    },
    {
      id: 'get-workflow',
      group: 'Workflows',
      title: 'Get workflow',
      body: function() {
        return h('div', null,
          h('div', { className: 'adoc-endpoint-row' },
            h(MethodBadge, { method: 'GET' }),
            h('code', { className: 'adoc-endpoint-path' }, '/v1/workflows/:workflowId')
          ),
          h('p', { className: 'adoc-text' }, 'Returns the full workflow state including nodes, connections, and configuration.'),
          h('h3', { className: 'adoc-h3' }, 'Path parameters'),
          h(ParamTable, { params: [
            { name: 'workflowId', type: 'string', description: 'The workflow ID (e.g. wf_abc123).', required: true }
          ] }),
          h('h3', { className: 'adoc-h3' }, 'Request'),
          h(CodeBlock, { code: 'curl -H "X-API-Key: YOUR_KEY" \\\n  ' + base + '/v1/workflows/wf_abc123' }),
          h('h3', { className: 'adoc-h3' }, 'Response'),
          h(ResponseBlock, { status: 200, label: 'Success', body: '{\n  "data": {\n    "workflowName": "My Workflow",\n    "aiInstruction": "",\n    "nodes": [\n      {\n        "id": "node_1",\n        "type": "httpRequest",\n        "label": "Fetch Data",\n        "config": { ... }\n      }\n    ],\n    "connections": [\n      { "from": "node_1", "to": "node_2" }\n    ],\n    "tags": []\n  }\n}' }),
          h(ResponseBlock, { status: 404, label: 'Not found', body: '{\n  "error": "Workflow not found."\n}' })
        );
      }
    }
  ];
}

/* ── sidebar nav grouping ───────────────────────────────── */

function buildNav(sections) {
  var groups = [];
  var groupMap = {};
  sections.forEach(function(s) {
    if (!groupMap[s.group]) {
      groupMap[s.group] = [];
      groups.push(s.group);
    }
    groupMap[s.group].push(s);
  });
  return { groups: groups, groupMap: groupMap };
}

/* ── main component ─────────────────────────────────────── */

function ApiDocsPage() {
  var sections = buildSections();
  var nav = buildNav(sections);
  var [activeId, setActiveId] = useState(sections[0].id);
  var contentRef = useRef(null);

  function scrollToSection(id) {
    setActiveId(id);
    var el = document.getElementById('adoc-' + id);
    if (el && contentRef.current) {
      contentRef.current.scrollTo({ top: el.offsetTop - 24, behavior: 'smooth' });
    }
  }

  useEffect(function() {
    var container = contentRef.current;
    if (!container) return;

    function handleScroll() {
      var scrollTop = container.scrollTop + 80;
      var current = sections[0].id;
      for (var i = 0; i < sections.length; i++) {
        var el = document.getElementById('adoc-' + sections[i].id);
        if (el && el.offsetTop <= scrollTop) {
          current = sections[i].id;
        }
      }
      setActiveId(current);
    }

    container.addEventListener('scroll', handleScroll, { passive: true });
    return function() { container.removeEventListener('scroll', handleScroll); };
  }, [sections]);

  return h('div', { className: 'adoc-root' },
    h('aside', { className: 'adoc-nav' },
      h('div', { className: 'adoc-nav__header' },
        h('span', { className: 'adoc-nav__brand' }, 'Alleflow'),
        h('span', { className: 'adoc-nav__badge' }, 'DOCS')
      ),
      nav.groups.map(function(group) {
        return h('div', { key: group, className: 'adoc-nav__group' },
          h('div', { className: 'adoc-nav__group-title' }, group),
          nav.groupMap[group].map(function(s) {
            return h('button', {
              key: s.id,
              type: 'button',
              className: 'adoc-nav__item' + (activeId === s.id ? ' adoc-nav__item--active' : ''),
              onClick: function() { scrollToSection(s.id); }
            }, s.title);
          })
        );
      })
    ),

    h('div', { className: 'adoc-main', ref: contentRef },
      sections.map(function(s) {
        return h('section', { key: s.id, id: 'adoc-' + s.id, className: 'adoc-section' },
          h('div', { className: 'adoc-section__eyebrow' }, s.group),
          h('h2', { className: 'adoc-section__title' }, s.title),
          s.body()
        );
      })
    )
  );
}

module.exports = ApiDocsPage;
